/**
 * Job Data Hooks
 * React Query hooks for fetching and managing job data
 *
 * Following DDD Principles:
 * - Hooks consume DDD provider-jobs service
 * - React Query handles caching and state management
 * - Components receive view models ready for display
 * - NO business logic in hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { providerJobsService } from '@/services/provider-jobs.service';
import { mapToViewModel, mapToViewModelList } from '../mappers/JobViewModelMapper';
import type { JobFilters, CreateJobInput, RescheduleJobInput } from '@/types/provider-job';
import type { JobCardViewModel, JobListViewModel } from '../view-models/JobListViewModel';

/**
 * Job query key factory
 * Provides consistent cache keys for React Query
 */
export const jobKeys = {
  all: ['provider-jobs'] as const,
  lists: () => [...jobKeys.all, 'list'] as const,
  list: (filters?: JobFilters) => [...jobKeys.lists(), { filters }] as const,
  details: () => [...jobKeys.all, 'detail'] as const,
  detail: (id: string) => [...jobKeys.details(), id] as const,
};

/**
 * Hook to fetch job list with optional filters
 * Maps DTOs to view models automatically
 */
export function useJobList(filters?: {
  status?: string;
  dateRange?: string;
  sortBy?: string;
}): ReturnType<typeof useQuery<JobListViewModel, Error>> {
  return useQuery<JobListViewModel, Error>({
    queryKey: jobKeys.list(filters as JobFilters),
    queryFn: async () => {
      // Convert frontend filter format to backend JobFilters
      const backendFilters: JobFilters = {};
      if (filters?.status && filters.status !== 'all') {
        backendFilters.status = filters.status as JobFilters['status'];
      }
      // Note: dateRange and sortBy would need backend support
      // For now, we'll filter client-side if needed

      const jobs = await providerJobsService.listJobs(backendFilters);
      const viewModels = mapToViewModelList(jobs);

      // Build filters view model
      const statusCounts = viewModels.reduce(
        (acc, job) => {
          acc[job.status] = (acc[job.status] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>
      );

      return {
        jobs: viewModels,
        filters: {
          statuses: [
            {
              value: 'all',
              label: 'All Jobs',
              count: viewModels.length,
              isActive: !filters?.status || filters.status === 'all',
            },
            {
              value: 'scheduled',
              label: 'Scheduled',
              count: statusCounts.scheduled || 0,
              isActive: filters?.status === 'scheduled',
            },
            {
              value: 'in_progress',
              label: 'In Progress',
              count: statusCounts.in_progress || 0,
              isActive: filters?.status === 'in_progress',
            },
            {
              value: 'completed',
              label: 'Completed',
              count: statusCounts.completed || 0,
              isActive: filters?.status === 'completed',
            },
            {
              value: 'cancelled',
              label: 'Cancelled',
              count: statusCounts.cancelled || 0,
              isActive: filters?.status === 'cancelled',
            },
          ],
          dateRanges: [
            {
              value: 'all',
              label: 'All Time',
              isActive: !filters?.dateRange || filters.dateRange === 'all',
            },
            {
              value: 'today',
              label: 'Today',
              isActive: filters?.dateRange === 'today',
            },
            {
              value: 'week',
              label: 'This Week',
              isActive: filters?.dateRange === 'week',
            },
            {
              value: 'month',
              label: 'This Month',
              isActive: filters?.dateRange === 'month',
            },
          ],
          sortOptions: [
            {
              value: 'date-desc',
              label: 'Newest First',
              isActive: !filters?.sortBy || filters.sortBy === 'date-desc',
            },
            {
              value: 'date-asc',
              label: 'Oldest First',
              isActive: filters?.sortBy === 'date-asc',
            },
            {
              value: 'name-asc',
              label: 'Name (A-Z)',
              isActive: filters?.sortBy === 'name-asc',
            },
            {
              value: 'status',
              label: 'By Status',
              isActive: filters?.sortBy === 'status',
            },
          ],
        },
        pagination: {
          currentPage: 1,
          totalPages: 1,
          totalItems: viewModels.length,
          itemsPerPage: viewModels.length,
          hasNextPage: false,
          hasPreviousPage: false,
        },
        summary: {
          displayText: `Showing all ${viewModels.length} ${viewModels.length === 1 ? 'job' : 'jobs'}`,
          totalCount: viewModels.length,
          filteredCount: viewModels.length,
        },
        isLoading: false,
        isEmpty: viewModels.length === 0,
        emptyStateMessage:
          !filters?.status || filters.status === 'all'
            ? 'No jobs yet. Create a job to get started.'
            : `No ${filters.status} jobs found.`,
      };
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    retry: 2,
  });
}

/**
 * Hook to fetch single job by ID
 */
export function useJob(jobId: string | undefined): ReturnType<typeof useQuery<JobCardViewModel, Error>> {
  return useQuery<JobCardViewModel, Error>({
    queryKey: jobKeys.detail(jobId ?? ''),
    queryFn: async () => {
      if (!jobId) {
        throw new Error('Job ID is required');
      }
      const job = await providerJobsService.getJob(jobId);
      return mapToViewModel(job);
    },
    enabled: !!jobId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
  });
}

/**
 * Hook to create a new job
 */
export function useCreateJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateJobInput) => providerJobsService.createJob(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.all });
    },
  });
}

/**
 * Hook to start a job
 */
export function useStartJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => providerJobsService.startJob(jobId),
    onSuccess: (_, jobId) => {
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
    },
  });
}

/**
 * Hook to complete a job
 */
export function useCompleteJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, notes }: { jobId: string; notes?: string }) =>
      providerJobsService.completeJob(jobId, notes),
    onSuccess: (_, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
    },
  });
}

/**
 * Hook to cancel a job
 */
export function useCancelJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, reason }: { jobId: string; reason: string }) =>
      providerJobsService.cancelJob(jobId, reason),
    onSuccess: (_, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
    },
  });
}

/**
 * Hook to reschedule a job
 *
 * Implements optimistic updates with rollback on error.
 * Perfect for drag-drop calendar interactions.
 */
export function useRescheduleJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      jobId,
      schedule,
    }: {
      jobId: string;
      schedule: RescheduleJobInput;
    }) => providerJobsService.rescheduleJob(jobId, schedule),

    // Optimistic update: Update cache immediately before server response
    onMutate: async ({ jobId, schedule }) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: jobKeys.lists() });
      await queryClient.cancelQueries({ queryKey: jobKeys.detail(jobId) });

      // Snapshot previous state for rollback
      const previousJobLists = queryClient.getQueryData(jobKeys.lists());
      const previousJobDetail = queryClient.getQueryData(jobKeys.detail(jobId));

      // Optimistically update job lists cache
      queryClient.setQueryData<JobListViewModel>(jobKeys.lists(), (old) => {
        if (!old) return old;

        return {
          ...old,
          jobs: old.jobs.map((job) =>
            job.id === jobId
              ? {
                  ...job,
                  startDate: schedule.scheduledDate,
                  startDateTimestamp: new Date(schedule.scheduledDate).getTime(),
                  scheduleText: new Date(schedule.scheduledDate).toLocaleDateString(),
                }
              : job
          ),
        };
      });

      // Return context for rollback
      return { previousJobLists, previousJobDetail };
    },

    // On error: Rollback to previous state
    onError: (_err, _variables, context) => {
      if (context?.previousJobLists) {
        queryClient.setQueryData(jobKeys.lists(), context.previousJobLists);
      }
      if (context?.previousJobDetail) {
        queryClient.setQueryData(
          jobKeys.detail(_variables.jobId),
          context.previousJobDetail
        );
      }
    },

    // Always refetch after success or error to ensure consistency
    onSettled: (_, __, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
    },
  });
}

/**
 * Hook to assign resources to a job
 */
export function useAssignResources() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      jobId,
      resources,
    }: {
      jobId: string;
      resources: {
        containerId?: string;
        vehicleId?: string;
        driverId?: string;
      };
    }) => providerJobsService.assignResources(jobId, resources),
    onSuccess: (_, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      // Also invalidate resource queries if they exist
      queryClient.invalidateQueries({ queryKey: ['containers'] });
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
    },
  });
}

/**
 * Hook to unassign a resource from a job
 */
export function useUnassignResource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      jobId,
      type,
    }: {
      jobId: string;
      type: 'container' | 'vehicle' | 'driver';
    }) => providerJobsService.unassignResource(jobId, type),
    onSuccess: (_, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      // Also invalidate resource queries
      queryClient.invalidateQueries({ queryKey: ['containers'] });
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['drivers'] });
    },
  });
}

/**
 * Hook to delete a job
 */
export function useDeleteJob() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => providerJobsService.deleteJob(jobId),
    onSuccess: (_, jobId) => {
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
    },
  });
}

/**
 * Hook to update custom field values on a job
 *
 * Updates custom fields based on job type definition.
 * Validates fields against job type schema.
 */
export function useUpdateJobCustomFields() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      jobId,
      customFields,
    }: {
      jobId: string;
      customFields: Record<string, unknown>;
    }) => providerJobsService.updateJobCustomFields(jobId, customFields),
    onSuccess: (_, { jobId }) => {
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
    },
  });
}

/**
 * Convenience hooks for common queries
 */

export function useAllJobs() {
  return useJobList();
}

export function useActiveJobs() {
  return useJobList({ status: 'in_progress' });
}

export function useScheduledJobs() {
  return useJobList({ status: 'scheduled' });
}

export function useCompletedJobs() {
  return useJobList({ status: 'completed' });
}
